/**
 * ScheduledRunReviewDrawer — 期产物人审闭环视图（波次 2）。
 *
 * 挂在 ScheduledTaskDetailDrawer 的执行历史 run 行「查看期产物」之后，叠加为
 * 二级抽屉。数据源 GET /scheduled-tasks/{taskId}/runs/{runId}/review，一个载荷
 * 齐备四块内容：
 *
 * 1. 步骤产物列表——每个步骤一张卡，卡脚是逐条表态控件（复用
 *    MessageVerdictControl 的交互口径：认同一点即成；不认同先落库、后采集
 *    选填理由，分类词表与全站七项一致）。表态走统一端点 POST /ai-verdicts，
 *    verdict 取契约的 agree/disagree 两值，无撤销——改判即再写一条新行。
 * 2. 已另存文档卡——本期产物另存后的落档位置；「另存为文档」动作成功后以
 *    W2-3 动作组（通知浮出「发起审查」/「与上一版比对」）承接，比对目标由
 *    响应的 compareWithPreviousParams 给出，无上期 accepted 产物时为 null、
 *    按钮隐藏。
 * 3. 上期对照摘要——previousRun 只读展示，供整期采纳前比对参照。
 * 4. 底部整期操作条——采纳（备注选填）/ 退回（理由必填，弹层先行校验）；
 *    reviewStatus 非 pending_review 或 canReview 为假时整条只读，展示状态
 *    与留痕（采纳备注 / 退回原因 / 操作人 / 时间）。
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  App,
  Button,
  Drawer,
  Empty,
  Input,
  List,
  Modal,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  useAcceptScheduledRun,
  useCreateAiVerdict,
  useCreateComparison,
  useReturnScheduledRun,
  useSaveScheduledRunAsDocument,
  useScheduledRunReview,
} from "../../api/queries";
import { mapApiError } from "../../utils/api-error";
import {
  SCHEDULED_RUN_REVIEW_STATUS_LABELS,
  type ScheduledRunItemVerdict,
  type ScheduledRunSaveAsDocumentResult,
  type ScheduledRunStepOutput,
} from "../../types/scheduledTask";
import type { AiVerdictReasonCode } from "../../types/aiReview";
import { formatDateTime } from "../../utils/date";
import EmptyState from "../common/EmptyState";
import { SkeletonGrid } from "../common/Skeleton";
import VerdictReasonPanel, {
  VerdictReasonSummary,
  type VerdictReasonPayload,
} from "../common/VerdictReasonPanel";
import {
  RUN_REVIEW_STATUS_TAG_COLOR,
  RUN_STATUS_TAG_COLOR,
  describeTaskActionError,
} from "./constants";
import {
  SCHEDULED_TASK_RUN_STATUS_LABELS,
  type ScheduledRunReviewStatus,
} from "../../types/scheduledTask";

const { Paragraph, Text } = Typography;

// ---------------------------------------------------------------------------
// 逐条表态控件
// ---------------------------------------------------------------------------

/**
 * 单个步骤产物的表态条。交互复刻 MessageVerdictControl：未表态时
 * 「认同 / 不认同」两个入口；不认同点击即落库，随后展开选填理由面板；
 * 已表态时给标签 + 改判出口（无撤销——统一表态表没有墓碑语义）。
 */
function RunItemVerdictBar({
  step,
  verdict,
  pending,
  onVote,
}: {
  step: ScheduledRunStepOutput;
  verdict: ScheduledRunItemVerdict | undefined;
  pending: boolean;
  onVote: (vote: "agree" | "disagree", payload?: VerdictReasonPayload) => void;
}) {
  const [reasonOpen, setReasonOpen] = useState(false);
  const key = step.key;
  const disagreed = verdict?.verdict === "disagree";

  if (!verdict) {
    return (
      <Space size="small" data-testid={`run-item-verdict-${key}`}>
        <Button size="small" disabled={pending} onClick={() => onVote("agree")}>
          认同
        </Button>
        <Button
          size="small"
          disabled={pending}
          onClick={() => {
            onVote("disagree");
            setReasonOpen(true);
          }}
        >
          不认同
        </Button>
      </Space>
    );
  }

  return (
    <>
      <Space size="small" data-testid={`run-item-verdict-${key}`}>
        <Tag color={disagreed ? "red" : "green"}>{verdict.verdict === "agree" ? "已认同" : "已不认同"}</Tag>
        <Button
          size="small"
          disabled={pending}
          onClick={() => {
            onVote(verdict.verdict === "agree" ? "disagree" : "agree");
            setReasonOpen(verdict.verdict === "agree");
          }}
        >
          {verdict.verdict === "agree" ? "改为不认同" : "改为认同"}
        </Button>
      </Space>
      {disagreed &&
        (reasonOpen ? (
          <VerdictReasonPanel
            reasonCode={(verdict.category as AiVerdictReasonCode | null) ?? undefined}
            reasonText={verdict.reason}
            pending={pending}
            onChange={(payload) => onVote("disagree", payload)}
            onSkip={() => setReasonOpen(false)}
            testIdPrefix="run-item"
            idSuffix={key}
          />
        ) : (
          <VerdictReasonSummary
            reasonCode={(verdict.category as AiVerdictReasonCode | null) ?? undefined}
            reasonText={verdict.reason}
            onOpen={() => setReasonOpen(true)}
            testIdPrefix="run-item"
            idSuffix={key}
          />
        ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main drawer
// ---------------------------------------------------------------------------

interface ScheduledRunReviewDrawerProps {
  taskId: string | null;
  runId: string | null;
  open: boolean;
  onClose: () => void;
}

export default function ScheduledRunReviewDrawer({
  taskId,
  runId,
  open,
  onClose,
}: ScheduledRunReviewDrawerProps) {
  const navigate = useNavigate();
  const { notification, message } = App.useApp();
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  const [acceptNote, setAcceptNote] = useState("");

  const reviewQuery = useScheduledRunReview(
    open && taskId && runId ? { taskId, runId } : null,
  );
  const review = reviewQuery.data;

  const verdictMut = useCreateAiVerdict();
  const acceptMut = useAcceptScheduledRun();
  const returnMut = useReturnScheduledRun();
  const saveMut = useSaveScheduledRunAsDocument();
  const compareMut = useCreateComparison();

  // 关闭再打开（切换 run）时清空操作条草稿，避免上一期的备注/理由残影。
  useEffect(() => {
    if (open) {
      setAcceptNote("");
      setReturnReason("");
      setReturnOpen(false);
    }
  }, [open, runId]);

  const handleVote = (
    step: ScheduledRunStepOutput,
    vote: "agree" | "disagree",
    payload?: VerdictReasonPayload,
  ) => {
    if (!taskId || !runId) return;
    verdictMut.mutate(
      {
        taskId,
        runId,
        targetKind: "scheduled_run_item",
        targetId: runId,
        targetRef: step.key,
        verdict: vote,
        reason: payload?.reasonText?.trim() || null,
        category: payload?.reasonCode ?? null,
      },
      {
        onError: (err) => message.error(mapApiError(err)),
      },
    );
  };

  /** W2-3 动作组：另存成功后浮出直达动作。比对参数无上期时为 null，按钮隐藏。 */
  const notifySaved = (result: ScheduledRunSaveAsDocumentResult) => {
    const key = `run-save-${result.documentId}-${Date.now()}`;
    const handleCompare = async () => {
      const params = result.compareWithPreviousParams;
      if (!params) return;
      notification.destroy(key);
      try {
        const created = await compareMut.mutateAsync({
          sourceDocumentId: params.sourceDocumentId,
          targetDocumentId: params.targetDocumentId,
        });
        if (created.status === "error") {
          message.error("比对引擎处理失败");
          return;
        }
        navigate(
          `/workbench/${result.documentId}?mode=compare&cmp=${created.veridocComparisonId}`,
        );
      } catch (err) {
        message.error(mapApiError(err, "compare"));
      }
    };
    notification.success({
      key,
      message: "本期产物已另存为事项文档",
      description: "可发起审查，或与上一版（上期采纳产物）比对。",
      placement: "topRight",
      btn: (
        <Space>
          {result.compareWithPreviousParams && (
            <Button
              size="small"
              data-testid="run-save-notify-compare-btn"
              onClick={() => void handleCompare()}
            >
              与上一版比对
            </Button>
          )}
          <Button
            size="small"
            type="primary"
            data-testid="run-save-notify-review-btn"
            onClick={() => {
              notification.destroy(key);
              navigate(`/workbench/${result.documentId}`);
            }}
          >
            发起审查
          </Button>
        </Space>
      ),
    });
  };

  const handleSaveAsDocument = async () => {
    if (!taskId || !runId) return;
    try {
      const result = await saveMut.mutateAsync({ taskId, runId });
      notifySaved(result);
    } catch (err) {
      message.error(describeTaskActionError(err, "另存失败，请稍后重试"));
    }
  };

  const handleAccept = async () => {
    if (!taskId || !runId) return;
    try {
      await acceptMut.mutateAsync({ taskId, runId, note: acceptNote.trim() || null });
      message.success("本期产物已采纳");
    } catch (err) {
      message.error(describeTaskActionError(err, "采纳失败，请刷新后重试"));
    }
  };

  const handleReturn = async () => {
    if (!taskId || !runId) return;
    const reason = returnReason.trim();
    if (!reason) {
      message.warning("退回必须填写理由");
      return;
    }
    try {
      await returnMut.mutateAsync({ taskId, runId, reason });
      message.success("本期产物已退回");
      setReturnOpen(false);
    } catch (err) {
      message.error(describeTaskActionError(err, "退回失败，请刷新后重试"));
    }
  };

  const readonly = !review || review.reviewStatus !== "pending_review" || !review.canReview;

  return (
    <Drawer
      title="期产物审核"
      open={open}
      onClose={onClose}
      width={720}
      destroyOnHidden
      data-testid="scheduled-run-review"
    >
      {reviewQuery.isLoading ? (
        <SkeletonGrid count={4} />
      ) : reviewQuery.isError || !review ? (
        <EmptyState
          title="加载失败"
          description="无法获取本期产物，请稍后重试。"
          action={
            <Button type="primary" onClick={() => reviewQuery.refetch()}>
              重试
            </Button>
          }
        />
      ) : (
        <div>
          {/* 状态行 */}
          <Space size="small" wrap style={{ marginBottom: "var(--jerry-sp-3)" }}>
            <Tag color={RUN_STATUS_TAG_COLOR[review.run.status]}>
              {SCHEDULED_TASK_RUN_STATUS_LABELS[review.run.status]}
            </Tag>
            <Tag
              color={RUN_REVIEW_STATUS_TAG_COLOR[review.reviewStatus]}
              data-testid="run-review-status-tag"
            >
              {SCHEDULED_RUN_REVIEW_STATUS_LABELS[review.reviewStatus]}
            </Tag>
            <Text type="secondary">计划时间 {formatDateTime(review.run.scheduledAt)}</Text>
          </Space>

          {/* 上期对照（只读） */}
          {review.previousRun ? (
            <Paragraph type="secondary" data-testid="run-review-previous-run">
              上期已采纳：
              {review.previousRun.acceptedAt
                ? ` ${formatDateTime(review.previousRun.acceptedAt)}`
                : " 时间未知"}
              {review.previousRun.savedDocumentIds.length > 0 &&
                `，另存 ${review.previousRun.savedDocumentIds.length} 份文档`}
              。本期另存后可与上期产物比对。
            </Paragraph>
          ) : (
            <Paragraph type="secondary">首期运行，暂无上期采纳产物可比对。</Paragraph>
          )}

          {/* 终态留痕（只读） */}
          {review.reviewStatus !== "pending_review" && (
            <Paragraph data-testid="run-review-trail">
              {review.reviewStatus === "accepted" && review.acceptedNote && (
                <>采纳备注：{review.acceptedNote}</>
              )}
              {review.reviewStatus === "returned" && review.returnReason && (
                <>退回原因：{review.returnReason}</>
              )}
              {review.reviewedBy && (
                <Text type="secondary">
                  {" "}
                  · {review.reviewedBy}
                  {review.reviewedAt ? ` ${formatDateTime(review.reviewedAt)}` : ""}
                </Text>
              )}
            </Paragraph>
          )}

          {/* 步骤产物 */}
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            步骤产物
          </Typography.Title>
          <List
            dataSource={review.stepOutputs}
            locale={{
              emptyText: <Empty description="本期运行没有步骤产物" />,
            }}
            renderItem={(step) => {
              const verdict = review.verdicts.find((v) => v.targetRef === step.key);
              return (
                <List.Item
                  key={step.key}
                  data-testid={`run-review-step-${step.key}`}
                  style={{ display: "block", padding: "var(--jerry-sp-3) 0" }}
                >
                  <Paragraph style={{ fontWeight: 600, marginBottom: "var(--jerry-sp-1)" }}>
                    {step.title || step.stepName || `步骤 ${step.key}`}
                  </Paragraph>
                  {step.summary && (
                    <Paragraph type="secondary" style={{ marginBottom: "var(--jerry-sp-1)" }}>
                      {step.summary}
                    </Paragraph>
                  )}
                  {step.content && (
                    <Paragraph
                      ellipsis={{ rows: 4, expandable: true, symbol: "展开" }}
                      style={{ marginBottom: "var(--jerry-sp-2)" }}
                    >
                      {step.content}
                    </Paragraph>
                  )}
                  <RunItemVerdictBar
                    step={step}
                    verdict={verdict}
                    pending={verdictMut.isPending}
                    onVote={(vote, payload) => handleVote(step, vote, payload)}
                  />
                </List.Item>
              );
            }}
          />

          {/* 已另存文档 */}
          <Typography.Title level={5}>已另存文档</Typography.Title>
          <List
            dataSource={review.savedDocuments}
            locale={{ emptyText: <Empty description="尚未另存。采纳前可先另存本期产物。" /> }}
            renderItem={(doc) => (
              <List.Item
                key={doc.id}
                data-testid={`run-review-saved-doc-${doc.id}`}
                actions={[
                  <Button
                    key="open"
                    type="link"
                    size="small"
                    onClick={() => navigate(`/workbench/${doc.id}`)}
                  >
                    打开工作台
                  </Button>,
                ]}
              >
                <Space direction="vertical" size={0}>
                  <span>{doc.title}</span>
                  {doc.createdAt && (
                    <Text type="secondary">{formatDateTime(doc.createdAt)}</Text>
                  )}
                </Space>
              </List.Item>
            )}
          />

          {/* 底部操作条 */}
          {readonly ? (
            review.reviewStatus === "pending_review" && !review.canReview ? (
              <Paragraph type="secondary" data-testid="run-review-readonly-hint">
                当前不可操作（整期审核仅一次，或权限受限）。
              </Paragraph>
            ) : null
          ) : (
            <div
              style={{
                marginTop: "var(--jerry-sp-4)",
                padding: "var(--jerry-sp-3)",
                background: "var(--jerry-gray-100)",
                borderRadius: 8,
              }}
              data-testid="run-review-action-bar"
            >
              <Space.Compact block style={{ marginBottom: "var(--jerry-sp-2)" }}>
                <Input
                  placeholder="采纳备注（选填）"
                  value={acceptNote}
                  onChange={(e) => setAcceptNote(e.target.value)}
                  data-testid="run-review-accept-note"
                />
              </Space.Compact>
              <Space>
                <Button
                  danger
                  onClick={() => setReturnOpen(true)}
                  data-testid="run-review-return-btn"
                >
                  退回
                </Button>
                <Button
                  onClick={handleSaveAsDocument}
                  loading={saveMut.isPending}
                  data-testid="run-review-save-as-btn"
                >
                  另存为文档
                </Button>
                <Button
                  type="primary"
                  loading={acceptMut.isPending}
                  onClick={handleAccept}
                  data-testid="run-review-accept-btn"
                >
                  采纳
                </Button>
              </Space>
            </div>
          )}

          {/* 退回理由弹层（必填先行校验） */}
          <Modal
            title="退回本期产物"
            open={returnOpen}
            onCancel={() => setReturnOpen(false)}
            onOk={handleReturn}
            okText="退回"
            okButtonProps={{ danger: true }}
            confirmLoading={returnMut.isPending}
            destroyOnHidden
            data-testid="run-review-return-modal"
          >
            <Paragraph type="secondary">退回理由必填，将随执行历史留痕。</Paragraph>
            <Input.TextArea
              rows={3}
              maxLength={2000}
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              placeholder="说明需要补充或修正的内容"
              data-testid="run-review-return-reason"
            />
          </Modal>
        </div>
      )}
    </Drawer>
  );
}
